/**
 * Who is allowed in. Pure — no network, no clock, no globals.
 *
 * ANYONE SIGNED IN IS ALLOWED, as of 2026-09-14. There is no sign-up surface in this app
 * — `App.tsx` renders `<SignIn>` only — so being authenticated already means holding a
 * 240 account, and per-user spend is capped by `core/quota.ts` rather than by the door.
 * The URL is not a secret, and a tool that turns away someone who already has the link
 * buys nothing: thirteen teachers clicked through from the Preview listing on 2026-09-14
 * and every one was refused by the gate that used to be here.
 *
 * What remains is a KILL SWITCH, not a gate, and the direction is the whole point. The old
 * gate failed CLOSED — an unset Statsig key shut the tool for months while looking like a
 * deliberate decision, and a flag dependency that could not resolve then admitted nobody.
 * `disabled` fails OPEN: unreadable, unset or unreachable all mean "not disabled", so no
 * configuration mistake can lock anyone out again. Turning it on is a deliberate act.
 *
 * A 240 address is never affected by the switch. The domain route is the stabler reason —
 * a flag can be flipped tomorrow and the answer for a 240 address should not change —
 * which also means flipping the switch never locks the team out of diagnosing why.
 */

/**
 * The same pair `jobapp` checks. Matching is on the whole domain, not a suffix — a suffix
 * test lets `a@240tutoring.com.evil.test` through, and that is the mistake this constant
 * and its tests exist to prevent.
 */
export const ALLOWED_EMAIL_DOMAINS = ['240tutoring.com', '240certification.com'] as const;

export type AccessDecision =
  | { allowed: true; via: 'domain' | 'signed_in' }
  | { allowed: false; reason: 'disabled' };

export interface AccessInput {
  /** From the verified token's claims. Absent is a real case, not an error. */
  email?: string | null;
  /**
   * The kill switch, already resolved. Resolving it is the shell's job, and the shell
   * resolves it to `false` on every failure — see `isDisabled` in worker/analytics.ts.
   */
  disabled: boolean;
}

/**
 * The domain part of an address, or null if there is not exactly one `@`.
 *
 * Exported because `core/quota.ts` needs the same parse for its own, narrower domain list.
 * The list differs; the parsing must not — a second implementation is a second chance to
 * get the suffix rule wrong, which is the whole reason `ALLOWED_EMAIL_DOMAINS` matches on
 * the complete domain rather than on a suffix.
 */
export function domainOf(email: string): string | null {
  const parts = email.split('@');
  if (parts.length !== 2) return null;
  const [local, domain] = parts as [string, string];
  if (local.length === 0 || domain.length === 0) return null;
  return domain;
}

export function decideAccess(
  { email, disabled }: AccessInput,
  allowedDomains: readonly string[] = ALLOWED_EMAIL_DOMAINS,
): AccessDecision {
  const normalized = (email ?? '').trim().toLowerCase();

  // Domain first, and it outranks the switch. A 240 address gets the same answer whatever
  // the flag says, which is what makes the switch safe to flip during an incident.
  if (normalized.length > 0) {
    const domain = domainOf(normalized);
    if (domain !== null && allowedDomains.includes(domain)) {
      return { allowed: true, via: 'domain' };
    }
  }

  if (disabled) return { allowed: false, reason: 'disabled' };

  // No address is no longer a refusal. Access does not depend on one any more, and turning
  // someone away for a claim their JWT template happens not to carry was never telling
  // them anything they could act on.
  return { allowed: true, via: 'signed_in' };
}
